import { useState, useEffect } from "react";
import {
  Text,
  Avatar,
  TagGroup,
  Tag,
  ActionButton,
  Image,
  CustomDialog,
  CloseButton,
  Skeleton,
} from "@react-spectrum/s2";

import ChevronLeftIcon from "@react-spectrum/s2/icons/ChevronLeft";
import ChevronRightIcon from "@react-spectrum/s2/icons/ChevronRight";

import { useStory } from "@app/hooks/stories";
import { useVisualUrl } from "@app/hooks/useAvatarUrl";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const detailStyles = style({
  font: "detail-lg",
  color: "neutral-subdued",
  marginTop: 0,
});

export const StoryPreviewDialog = ({ storyId }) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const storyQuery = useStory(storyId);
  const story = storyQuery.data;

  const slides = story?.script || [];
  const currentSlide = slides[currentSlideIndex];
  const currentPersonas =
    currentSlide?.personas?.map((idx) => story.personas[idx]) ?? [];

  // Get the visual URL for the current slide
  const currentVisual = currentSlide?.visuals?.[currentSlide.visual || 0];
  const visualUrl = useVisualUrl(currentVisual);

  const hasPreviousSlide = currentSlideIndex > 0;
  const hasNextSlide = currentSlideIndex < slides.length - 1;

  const goToPreviousSlide = () => {
    if (hasPreviousSlide) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const goToNextSlide = () => {
    if (hasNextSlide) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  // Reset to first slide when story changes
  useEffect(() => {
    setCurrentSlideIndex(0);
  }, [storyId]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          goToPreviousSlide();
          break;
        case "ArrowRight":
          event.preventDefault();
          goToNextSlide();
          break;
        case "Escape":
          // Close dialog - handled by CloseButton
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentSlideIndex, hasPreviousSlide, hasNextSlide]);

  if (!story) {
    return (
      <CustomDialog padding="none" styles={style({ zIndex: 10 })}>
        <div className={style({ padding: 32, textAlign: "center" })}>
          <Skeleton isLoading={true}>
            <Text>Loading story...</Text>
          </Skeleton>
        </div>
      </CustomDialog>
    );
  }

  return (
    <CustomDialog padding="none" styles={style({ zIndex: 10 })}>
      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          size: "full",
          position: "relative",
        })}
      >
        {/* Header with story info and navigation */}
        <div
          className={style({
            display: "flex",
            alignItems: "center",
            paddingStart: 16,
            paddingEnd: 56,
            paddingY: 12,
            borderBottom: "1px solid",
            borderColor: "gray-200",
            backgroundColor: "gray-100",
          })}
        >
          <div
            className={style({
              flexGrow: "1",
              flexShrink: "1",
              flexBasis: "[0%]",
              display: "flex",
              alignItems: "center",
              gap: 16,
              paddingEnd: 56,
            })}
          >
            <Text styles={style({ font: "detail", fontWeight: "bold" })}>
              {story.title}
            </Text>
          </div>

          <div
            className={style({
              display: "flex",
              gap: 12,
              alignItems: "center",
            })}
          >
            <ActionButton
              isQuiet
              isDisabled={!hasPreviousSlide}
              onPress={goToPreviousSlide}
              aria-label="Previous slide"
            >
              <ChevronLeftIcon />
            </ActionButton>
            <div
              className={style({
                display: "flex",
                alignItems: "center",
                gap: 2,
                font: "ui",
                color: "neutral-subdued",
              })}
            >
              <Text>{currentSlideIndex + 1}</Text>
              <Text styles={style({ color: "detail" })}>/</Text>
              <Text>{slides.length}</Text>
            </div>
            <ActionButton
              isQuiet
              isDisabled={!hasNextSlide}
              onPress={goToNextSlide}
              aria-label="Next slide"
            >
              <ChevronRightIcon />
            </ActionButton>
          </div>

          <div
            className={style({
              flexGrow: "1",
              flexShrink: "1",
              flexBasis: "[0%]",
            })}
          />
        </div>

        {/* Main content area */}
        <div
          className={style({
            display: "flex",
            flexDirection: {
              default: "column",
              sm: "row",
            },
            flexGrow: 1,
            overflow: "hidden",
          })}
        >
          {/* Visual area */}
          <div
            className={style({
              backgroundColor: "gray-100",
              size: "full",
            })}
          >
            {currentSlide?.visuals?.length > 0 ? (
              <Image
                src={visualUrl}
                alt={currentSlide.title}
                styles={style({
                  aspectRatio: "16 / 10",
                  objectFit: "contain",
                  userSelect: "none",
                  pointerEvents: "none",
                  overflow: "inherit",
                })}
              />
            ) : (
              <div
                className={style({
                  width: "full",
                  aspectRatio: "16 / 10",
                })}
              />
            )}
          </div>

          {/* Content area */}
          <div
            className={style({
              padding: {
                default: 24,
                sm: 32,
              },
              display: "flex",
              flexDirection: "column",
              flexBasis: {
                default: "auto",
                sm: 320,
              },
              flexShrink: 0,
              rowGap: 24,
              overflowY: "auto",
            })}
          >
            {/* Slide content */}
            {currentSlide && (
              <div>
                {/* Slide section */}
                <div
                  className={style({
                    font: "detail-xl",
                    marginBottom: 8,
                    color: "gray-600",
                  })}
                >
                  <Text>{currentSlide.section}</Text>
                </div>

                {/* Slide title */}
                <div className={style({ marginBottom: 12 })}>
                  <Text
                    styles={style({
                      font: "heading-lg",
                      fontWeight: "bold",
                      color: "gray-900",
                    })}
                  >
                    {currentSlide.title}
                  </Text>
                </div>

                {/* Slide description */}
                {currentSlide.description && (
                  <div className={style({ marginBottom: 16 })}>
                    <Text
                      styles={style({
                        font: "detail",
                        color: "gray-700",
                        fontStyle: "italic",
                      })}
                    >
                      {currentSlide.description}
                    </Text>
                  </div>
                )}

                {/* Slide text content */}
                <div
                  className={style({
                    font: "body-lg",
                    whiteSpace: "pre-line",
                    color: "gray-800",
                  })}
                >
                  <Text>{currentSlide.text}</Text>
                </div>

                {/* Personas */}
                {currentPersonas?.length > 0 && (
                  <div
                    className={style({
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginTop: 32,
                    })}
                  >
                    {currentPersonas.map((persona, idx) => (
                      <div
                        className={style({
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          marginTop: {
                            default: -8,
                            isFirst: 0,
                          },
                        })({ isFirst: idx === 0 })}
                        key={`${persona.id}-${idx}`}
                      >
                        <Avatar
                          size={32}
                          alt={persona?.name}
                          src={
                            persona?.avatarUrl ??
                            "https://mir-s3-cdn-cf.behance.net/project_modules/disp/690bc6105945313.5f84bfc9de488.png"
                          }
                          isOverBackground
                          styles={style({
                            borderWidth: 2,
                          })}
                        />
                        <Text styles={detailStyles}>{persona?.name}</Text>
                      </div>
                    ))}
                  </div>
                )}

                {/* Solutions */}
                {!!currentSlide?.solutions?.length && (
                  <TagGroup
                    selectionMode="none"
                    size="S"
                    aria-label="Solutions"
                    styles={style({ marginTop: 16 })}
                  >
                    {currentSlide.solutions.map((solution, idx) => (
                      <Tag key={`${solution}-${idx}`}>{solution}</Tag>
                    ))}
                  </TagGroup>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Close button */}
        <CloseButton
          styles={style({
            position: "absolute",
            top: 12,
            insetEnd: 12,
          })}
        />
      </div>
    </CustomDialog>
  );
};
